/* eslint-disable class-methods-use-this */
class RudderTracker {
	constructor() {
		this.storedEventKey = "storedEvents";
		this.identifyIdKey = "anonymousId";
		this.storedAnonymousId = localStorage.getItem(this.identifyIdKey);
		this.pageType = this.getPageType();
		this.localisation = { country: "GB", region: "gb-london", locale: "en" };
		this.testing = { variants: 0, variantsprismic_ab_test: 2, variantsexpress_checkout: 1 };
	}

	init() {
		this.ecommerce = this.getEcommerce();
		this.ecomm = this.getEcomm();
		this.experiments = this.getExperimentsFromCookie();

		// send failed events
		this.sendStoredEvents();

		this.sendEvent({ type: "identify" });
		this.sendEvent({
			type: "page",
			payload: {
				...this.ecomm,
				ecommerce: this.ecommerce,
				localisation: this.localisation,
				testing: this.testing,
				...this.experiments,
			},
		});

		window.addEventListener("beforeunload", this.sendStoredEvents.bind(this));
		document.addEventListener("visibilitychange", this.sendStoredEvents.bind(this));
	}

	getExperimentsFromCookie() {
		const match = document.cookie.match(/(^| )ab_exp=([^;]+)/);
		if (!match) return {};

		try {
			const parsed = JSON.parse(decodeURIComponent(match[2]));
			const experimentsObj = {};

			Object.values(parsed).forEach((entry) => {
				const [exp, variant] = entry.split(":");
				if (exp && variant) {
					experimentsObj[exp] = variant;
				}
			});

			const result = {};

			if (Object.keys(experimentsObj).length > 0) {
				// Object with all experiments
				result.experiments = experimentsObj;

				// String format(allows add only one dimension on GA4, but then more work on GA4 exploration)
				const expString = Object.keys(experimentsObj)
					.map((key) => {
						const cleanKey = key.replace(/[^a-zA-Z0-9]/g, "_");
						return `${cleanKey}:${experimentsObj[key]}`;
					})
					.join("|");

				result.experiments_string = expString;

				// Flatten dynamically for GA4( need a dimension for each exp)
				Object.keys(experimentsObj).forEach((key) => {
					const cleanKey = key.replace(/[^a-zA-Z0-9]/g, "_");
					result[`exp_${cleanKey}`] = experimentsObj[key];
				});
			}

			return result;
		} catch (e) {
			return {};
		}
	}

	getPageType() {
		const pageTypes = {
			pages: "page",
			products: "product",
			collections: "category",
		};
		const location = window.location.pathname;
		const path = location.split("/")[1];

		return pageTypes[path] || "page";
	}

	getEcommerce() {
		try {
			const dataListProducts = document.querySelectorAll("article[data-product]:not([hidden])");
			const listProducts = Array.from(dataListProducts)
				.flatMap((list) => {
					const data = list.getAttribute("data-product");
					if (data) {
						return JSON.parse(data);
					}
					return false;
				})
				.filter(Boolean);

			return { currencyCode: "GBP", impression: listProducts };
		} catch (error) {
			console.log(error);
			return { currencyCode: "GBP", impression: [] };
		}
	}

	getEcomm() {
		if (this.pageType === "product") return this.getEcommProductPage();
		if (this.pageType === "category") return this.getEcommCollectionPage();

		return { ecomm_totalvalue: 0, ecomm_prodid: [] };
	}

	getEcommProductPage() {
		try {
			const productForm = document.getElementById("variant-selector");
			const checkedOrFirst = productForm.querySelector("input:checked") || productForm.querySelector("input");
			const { productTitle, productType } = productForm.dataset;
			const { variantAvailable, price, variantSelectedOptions, sku } = checkedOrFirst.dataset;
			const variant = JSON.parse(variantSelectedOptions).map((opt) => opt.value);
			return {
				ecomm_pagetype: this.pageType,
				ecomm_totalvalue: parseFloat(price),
				ecomm_prodid: sku,
				product_type: productType,
				name: productTitle,
				variant: variant.join(" - "),
				available: variantAvailable,
				currency: "GBP",
				price: parseFloat(price),
			};
		} catch (error) {
			console.log(error);
			return {
				ecomm_totalvalue: 0,
				ecomm_prodid: [],
			};
		}
	}

	getEcommCollectionPage() {
		try {
			const location = window.location.pathname;
			const handle = location.split("/")[2];
			const dataListProducts = document.querySelectorAll("article[data-product]:not([hidden])");
			const variantList = Array.from(dataListProducts)
				.flatMap((list) => {
					const data = list.getAttribute("data-variant-skus");
					if (data) {
						return JSON.parse(data);
					}
					return false;
				})
				.filter(Boolean);

			return {
				ecomm_pagetype: this.pageType,
				ecomm_prodid: variantList,
				ecomm_totalvalue: 0,
				ecomm_category: handle,
			};
		} catch (error) {
			return {
				ecomm_totalvalue: 0,
				ecomm_prodid: [],
			};
		}
	}

	sendIdentifyEvent(event) {
		try {
			if (this.storedAnonymousId === "" || this.storedAnonymousId === null) {
				this.storedAnonymousId = window.rudderanalytics.getAnonymousId();
				localStorage.setItem(this.identifyIdKey, this.storedAnonymousId);
			}

			window.rudderanalytics.setAnonymousId(this.storedAnonymousId);
			window.rudderanalytics.identify(this.storedAnonymousId, {
				localisation: this.localisation,
				testing: this.testing,
			});
		} catch (error) {
			this.saveEvent(event);
		}
	}

	sendTrackEvent(event) {
		if (window.rudderanalytics) {
			const { payload } = event;
			const { event: eventName, properties = {} } = payload;

			// Amazon Tag is implemented outside Rudderstack
			// Sends amazon trackEvent before add rudderstack params
			if (window.amzn) window.amzn("trackEvent", eventName, properties);

			Object.assign(properties, {
				localisation: this.localisation,
				testing: this.testing,
				...this.experiments,
			});
			window.rudderanalytics.track(eventName, properties);
		} else {
			this.saveEvent(event);
		}
	}

	sendPageEvent(event) {
		const { payload } = event;
		if (!payload) return;
		try {
			window.rudderanalytics.page(payload);
		} catch (error) {
			this.saveEvent(event);
		}
	}

	sendAliasEvent(event) {
		if (window.rudderanalytics) {
			window.rudderanalytics.alias();
		} else {
			this.saveEvent(event);
		}
	}

	sendEvent(event) {
		try {
			window.rudderanalytics.ready(() => {
				this.sendStoredEvents();

				const { type } = event;
				switch (type) {
					case "identify":
						this.sendIdentifyEvent(event);
						break;
					case "track":
						this.sendTrackEvent(event);
						break;
					case "page":
						this.sendPageEvent(event);
						break;
					case "aliasEvent":
						this.sendAliasEvent(event);
						break;
					default:
						console.log("Event not recognized.");
						break;
				}
			});
		} catch (error) {
			this.saveEvent(event);
		}
	}

	sendStoredEvents() {
		const storedEvents = this.loadSavedEvents();
		if (storedEvents.length) console.log("Sending stored events", storedEvents.length);

		if (Array.isArray(storedEvents) && storedEvents.length) {
			this.clearStoredEvents();
			storedEvents.forEach((event) => {
				this.sendEvent(event);
			});
		}
	}

	saveEvent(event) {
		if (!event) return;
		const events = this.loadSavedEvents();
		events.push(event);
		localStorage.setItem(this.storedEventKey, JSON.stringify(events));
	}

	clearStoredEvents() {
		localStorage.removeItem(this.storedEventKey);
	}

	loadSavedEvents() {
		const storedEvents = localStorage.getItem(this.storedEventKey);
		if (storedEvents) return JSON.parse(storedEvents);

		return [];
	}
}

window.rudderTracker = new RudderTracker();
window.addEventListener("DOMContentLoaded", () => {
	window.rudderTracker.init();
});
